/**
 * useInsights Hook
 * Shared between web and mobile for fetching user insights, issues, and strategies
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import type { AnalyticsFilter } from '../analyticsScopes';
import { ApiError, type HttpClient } from '../api/httpClient';

// =============================================================================
// Types
// =============================================================================

interface PlayerPerformance {
  player: string;
  hits: number;
  total: number;
  hitRate: string;
}

interface PropPerformance {
  prop: string;
  hits: number;
  total: number;
  hitRate: string;
}

interface ParlaySizePerf {
  wins: number;
  losses: number;
  total?: number;
}

interface BiggestBet {
  amount: string | number;
  odds?: string;
  parlayId?: string;
  date?: string;
}

export interface AIInsightsAnalysis {
  overallAssessment: string;
  keyStrengths: string[];
  criticalWeaknesses: string[];
  personalizedTips: string[];
  riskProfile: 'conservative' | 'moderate' | 'aggressive';
  improvementPriorities: string[];
}

export interface UserInsights {
  totalBets: number;
  totalWins: number;
  totalLosses: number;
  totalWagered: string;
  totalProfit: string;
  winRate: string;
  roi: string;
  totalLegs: number;
  totalLegHits: number;
  legHitRate: string;
  overHitRate: string;
  underHitRate: string;
  parlaySizePerformance: Record<string, ParlaySizePerf>;
  oddsRangePerformance: Record<string, unknown>;
  bestPlayers: PlayerPerformance[];
  worstPlayers: PlayerPerformance[];
  bestProps: PropPerformance[];
  worstProps: PropPerformance[];
  leaguePerformance: Record<string, unknown>;
  temporalPatterns: Record<string, unknown>;
  currentStreak: number;
  longestWinStreak: number;
  longestLossStreak: number;
  biggestWin: BiggestBet | null;
  biggestLoss: BiggestBet | null;
  computedAt?: string;
  betCountAtComputation?: number;
  aiAnalysis?: AIInsightsAnalysis | null;
  aiAnalysisGeneratedAt?: string | null;
}

export interface Issue {
  id: number;
  issueType: string;
  category: string;
  title: string;
  description: string;
  data?: unknown;
  recommendation?: string;
  isActive?: boolean;
  firstDetectedAt?: string;
  dismissedAt?: string | null;
  aiExplanation?: string | null;
}

export interface Strategy {
  id: number;
  league?: string | null;
  betType?: string | null;
  title: string;
  description?: string;
  tips: string[];
  relevanceScore?: string | number;
  basedOn?: unknown;
  variant?: string;
  priority?: number;
  isActive?: boolean;
  aiNarrative?: string | null;
}

export interface GlobalInsight {
  id: number;
  category: string;
  title: string;
  content: {
    stats?: Array<{
      value?: string;
      winRate?: string;
      label?: string;
      description?: string;
      highlight?: boolean;
    }>;
    tips?: Array<{
      label: string;
      text: string;
    }>;
    note?: string;
  };
  variant:
    | 'red'
    | 'yellow'
    | 'green'
    | 'blue'
    | 'purple'
    | 'cyan'
    | 'orange'
    | 'indigo'
    | 'rose'
    | 'emerald'
    | 'amber'
    | 'teal'
    | 'gray';
  priority?: number;
  isActive?: boolean;
}

export interface RefreshResult {
  success: boolean;
  error?: string;
  [key: string]: unknown;
}

export interface DismissResult {
  success: boolean;
  error?: string;
}

export interface UseInsightsOptions {
  httpClient: HttpClient;
  isAuthenticated: boolean;
  filter?: AnalyticsFilter;
  autoLoad?: boolean;
}

export interface UseInsightsResult {
  insights: UserInsights | null;
  issues: Issue[];
  strategies: Strategy[];
  globalInsights: GlobalInsight[];
  loading: boolean;
  refreshing: boolean;
  error: string | null;
  refreshInsights: () => Promise<RefreshResult>;
  dismissIssue: (issueId: number) => Promise<DismissResult>;
  reload: () => Promise<void>;
}

// =============================================================================
// Hook
// =============================================================================

interface InsightsResponse {
  insights?: UserInsights;
  issues?: Issue[];
  strategies?: Strategy[];
  globalInsights?: GlobalInsight[];
}

/** Client-side stale-time: skip fetch if data was loaded within this window */
const STALE_TIME_MS = 5 * 60 * 1000; // 5 minutes

/** Per-filter cache of fetched data (module-level so it persists across mounts) */
interface CachedInsights {
  insights: UserInsights | null;
  issues: Issue[];
  strategies: Strategy[];
  globalInsights: GlobalInsight[];
  fetchedAt: number;
}
const insightsCache: Record<string, CachedInsights> = {};

/** Drop every cached filter (sign-out), so the next user's first read fetches their own. */
export function clearInsightsCache(): void {
  for (const key of Object.keys(insightsCache)) delete insightsCache[key];
}

export function useInsights(options: UseInsightsOptions): UseInsightsResult {
  const { httpClient, isAuthenticated, filter = 'all_tracked', autoLoad = true } = options;

  // Restore cached data for this filter immediately (avoids showing stale filter's data)
  const cached = insightsCache[filter];
  const [insights, setInsights] = useState<UserInsights | null>(cached?.insights ?? null);
  const [issues, setIssues] = useState<Issue[]>(cached?.issues ?? []);
  const [strategies, setStrategies] = useState<Strategy[]>(cached?.strategies ?? []);
  const [globalInsights, setGlobalInsights] = useState<GlobalInsight[]>(
    cached?.globalInsights ?? [],
  );
  const [loading, setLoading] = useState(!cached);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const prevFilterRef = useRef(filter);

  // When filter changes, restore cached data for the new filter immediately
  useEffect(() => {
    if (prevFilterRef.current !== filter) {
      prevFilterRef.current = filter;
      const c = insightsCache[filter];
      if (c) {
        setInsights(c.insights);
        setIssues(c.issues);
        setStrategies(c.strategies);
        setGlobalInsights(c.globalInsights);
      } else {
        setInsights(null);
        setIssues([]);
        setStrategies([]);
        setGlobalInsights([]);
      }
    }
  }, [filter]);

  const loadInsights = useCallback(async () => {
    // Skip fetch if this filter was recently loaded (stale-time check)
    const cacheKey = filter;
    const cachedEntry = insightsCache[cacheKey];
    if (cachedEntry && Date.now() - cachedEntry.fetchedAt < STALE_TIME_MS) {
      // Data already restored via state — just mark not loading
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const queryParams: Record<string, string> = {};
      if (filter === 'real_bets') queryParams.filter = 'real_bets';

      let newInsights: UserInsights | null = null;
      let newIssues: Issue[] = [];
      let newStrategies: Strategy[] = [];
      let newGlobalInsights: GlobalInsight[] = [];

      if (isAuthenticated) {
        const data = await httpClient.get<InsightsResponse>('/api/insights', queryParams);
        newInsights = data.insights || null;
        newIssues = data.issues || [];
        newStrategies = data.strategies || [];
        newGlobalInsights = data.globalInsights || [];
      } else {
        const data = await httpClient.get<{ globalInsights?: GlobalInsight[] }>(
          '/api/insights/global',
        );
        newGlobalInsights = data.globalInsights || [];
      }

      // Update state
      setInsights(newInsights);
      setIssues(newIssues);
      setStrategies(newStrategies);
      setGlobalInsights(newGlobalInsights);

      // Cache per filter
      insightsCache[cacheKey] = {
        insights: newInsights,
        issues: newIssues,
        strategies: newStrategies,
        globalInsights: newGlobalInsights,
        fetchedAt: Date.now(),
      };
    } catch (err) {
      const msg = ApiError.getUserMessage(err, 'Failed to load insights');
      console.error('Failed to load insights:', msg);
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [httpClient, isAuthenticated, filter]);

  const refreshInsights = useCallback(async (): Promise<RefreshResult> => {
    if (!isAuthenticated) return { success: false, error: 'Not authenticated' };

    setRefreshing(true);
    setError(null);

    try {
      const result = await httpClient.post<RefreshResult>('/api/insights/refresh');
      // Clear cache so reload fetches fresh data
      delete insightsCache[filter];
      await loadInsights();
      return { ...result, success: true };
    } catch (err) {
      const msg = ApiError.getUserMessage(err, 'Failed to refresh insights');
      console.error('Failed to refresh insights:', msg);
      setError(msg);
      return { success: false, error: msg };
    } finally {
      setRefreshing(false);
    }
  }, [httpClient, isAuthenticated, filter, loadInsights]);

  const dismissIssue = useCallback(
    async (issueId: number): Promise<DismissResult> => {
      if (!isAuthenticated) return { success: false };

      try {
        await httpClient.patch(`/api/insights/issues/${issueId}/dismiss`);
        setIssues((prev) => prev.filter((i) => i.id !== issueId));
        return { success: true };
      } catch (err) {
        const msg = ApiError.getUserMessage(err, 'Failed to dismiss issue');
        console.error('Failed to dismiss issue:', msg);
        return { success: false, error: msg };
      }
    },
    [httpClient, isAuthenticated],
  );

  useEffect(() => {
    if (autoLoad) {
      loadInsights();
    }
  }, [autoLoad, loadInsights]);

  return {
    insights,
    issues,
    strategies,
    globalInsights,
    loading,
    refreshing,
    error,
    refreshInsights,
    dismissIssue,
    reload: loadInsights,
  };
}
